/**
 * 小窝跟上 TA（HOME-WIRE-02，6c2b 10-13；UI 总账 S-031、W-39、Q-166）。
 *
 * 出门、到达、离开、回家以后，小窝原来要等下一次 60 秒刷新才变（实走 49–64 秒）。
 * 后端有 GET /home/version：这只宠物「此刻在做什么」相关事实的指纹，这几个时刻一到就变，只读、不推进世界。
 * 这里在前台每 10 秒拉一次版本号，变了就让小窝重读 /home，状态变化后十来秒页面就跟上。
 * - 切到后台就停（react-query 默认后台不按间隔拉）；回到前台马上拉一次（默认切回窗口就重拉）。
 * - 不手动带 If-None-Match：后端回 ETag 加 Cache-Control: no-cache，浏览器自己做条件请求，304 时把缓存的正文当 200 交回来，
 *   省流量的目的照样达到；也就不会有总账 Q-166 说的「回前台先拿 304、晚一轮」，共用请求客户端也不用改。
 * - 第一次拿到的版本号只记下、不触发重读（小窝自己刚读过）；之后变了才重读，同一只宠物才比，换宠物从头记。
 * - 版本号读不到（没有这个口的老后端、网络断）：退到 60 秒一次，不报错、不打扰；小窝自己的 60 秒刷新照旧兜底。
 * - 演示模式、或服务里没有这个读口（测试替身）：不轮询。
 */
import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";

export const HOME_VERSION_POLL_MS = 10_000;
export const HOME_VERSION_POLL_AFTER_ERROR_MS = 60_000;

/** 正常 10 秒一次；上一次读失败就 60 秒一次（恢复以后回到 10 秒）。 */
export function homeVersionInterval(query: { state: { status: string } }): number {
  return query.state.status === "error" ? HOME_VERSION_POLL_AFTER_ERROR_MS : HOME_VERSION_POLL_MS;
}

/** 和小窝的键（queryKeys.homeFor）分开：重读小窝时按小窝的键精确失效，不连带版本号。 */
export const homeVersionKey = (userId: string, petId: string) => ["world", "homeVersion", userId, petId] as const;

export function useHomeVersionSync(): void {
  const { world } = useServices();
  const household = useOptionalCurrentHousehold();
  const userId = household?.userId ?? null;
  const petId = household?.pet?.pet_id ?? null;
  const queryClient = useQueryClient();
  const read = env.dataMode === "fixture" ? undefined : world.homeVersion;
  const enabled = typeof read === "function" && Boolean(userId && petId);
  const { data } = useQuery({
    queryKey: homeVersionKey(userId ?? "-", petId ?? "-"),
    queryFn: ({ signal }) => read!(petId!, signal),
    enabled,
    refetchInterval: homeVersionInterval,
    refetchIntervalInBackground: false,
    retry: false,
    staleTime: 0,
  });
  const seen = useRef<{ key: string; version: string } | null>(null);
  const version = enabled && data?.pet_id === petId ? data.version : null;
  useEffect(() => {
    if (!version || !userId || !petId) return;
    const key = `${userId}|${petId}`;
    const before = seen.current;
    seen.current = { key, version };
    if (before && before.key === key && before.version !== version) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.homeFor(userId, petId), exact: true });
    }
  }, [version, userId, petId, queryClient]);
}
